/**
 * PlanDiagnosticsBetaPanel — compose из linked Diagnocat snapshot (parity MIS Plan Beta).
 * Без шагов КТ/submit: только proposal → review → apply → optional Gamma.
 */
import { useEffect, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import type { ConstructorPlan } from '../constructorTypes'
import { PlanDiagnosticsReviewPanel } from './PlanDiagnosticsReviewPanel'
import { PlanGammaPresentationStep } from './PlanGammaPresentationStep'
import {
  PLAN_DIAGNOSTICS_INITIAL_PROMPT,
  PLAN_DIAGNOSTICS_MODEL_CHOICE,
  isRestorableDiagnosticsProposal,
  recoverLatestDiagnosticsProposal,
} from './planDiagnosticsCompose'
import {
  WORKFLOW_CHECKPOINT_KEYS,
  formatCheckpointSavedAt,
  savePlanWorkflowCheckpoint,
  type PlanWorkflowCheckpoint,
} from '../workflowCheckpoint'
import { prefetchPresentationPhotoSources } from './planPresentation'

const ACTION_KEY = 'plan_diagnostics_auto_compose'
const UI_SURFACE = 'staff_lk_patient_plan_diagnostics_beta'

type AiProposal = {
  proposal_id: string
  proposed_payload?: Record<string, unknown> | null
  override_payload?: Record<string, unknown> | null
  missing_required_fields?: string[]
  apply_error_text?: string | null
}

function extractMisPlanId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const p = payload as Record<string, unknown>
  const apply = p.apply_result ?? p.result ?? p
  if (!apply || typeof apply !== 'object') return null
  const a = apply as Record<string, unknown>
  if (typeof a.target_entity_id === 'string') return a.target_entity_id
  const created = a.created_plan
  if (created && typeof created === 'object' && typeof (created as { plan_id?: string }).plan_id === 'string') {
    return (created as { plan_id: string }).plan_id
  }
  return null
}

export function PlanDiagnosticsBetaPanel({
  bootstrap,
  session,
  plan,
  misPatientId,
  staffUserId,
  diagnosticsLinked,
  onApplied,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  plan?: ConstructorPlan | null
  misPatientId: string | null
  staffUserId: string
  diagnosticsLinked: boolean
  onApplied?: () => void
}) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [proposal, setProposal] = useState<AiProposal | null>(null)
  const [appliedMisPlanId, setAppliedMisPlanId] = useState<string | null>(null)
  const [restoredFromCheckpoint, setRestoredFromCheckpoint] = useState(false)

  const checkpointKey = WORKFLOW_CHECKPOINT_KEYS.planDiagnosticsBeta
  const savedCheckpoint = plan?.workflow_checkpoints?.[checkpointKey] ?? null

  useEffect(() => {
    if (!plan?.id || restoredFromCheckpoint || proposal) return
    const cp = savedCheckpoint
    if (!cp || cp.step !== 'proposal_ready' || !cp.proposal) return
    if (!isRestorableDiagnosticsProposal(cp.proposal)) return
    setProposal(cp.proposal as AiProposal)
    setRestoredFromCheckpoint(true)
    if (cp.applied_mis_plan_id) setAppliedMisPlanId(cp.applied_mis_plan_id)
  }, [plan?.id, savedCheckpoint, restoredFromCheckpoint, proposal])

  const authHeader = { Authorization: `Bearer ${session.accessToken}` }
  const apiBase = bootstrap.apiBase

  const persistCheckpoint = async (payload: PlanWorkflowCheckpoint) => {
    if (!plan?.id) return
    await savePlanWorkflowCheckpoint(apiBase, session, plan.id, checkpointKey, payload)
  }

  const disabledReason = !misPatientId
    ? 'Не найден MIS patient_id (связка LK ↔ MIS по телефону пациента).'
    : !staffUserId
      ? 'Не выбран врач-автор.'
      : null
  const diagnocatHint = !diagnosticsLinked && misPatientId
    ? 'Diagnocat в LK не подтверждён — compose проверит snapshot в MIS.'
    : null

  const dismissDraft = () => {
    setProposal(null)
    setError(null)
    setAppliedMisPlanId(null)
    setRestoredFromCheckpoint(true)
    void persistCheckpoint({ step: 'dismissed', proposal: null, last_error: null })
  }

  const hasStaleDraft = Boolean(proposal && !appliedMisPlanId && !isRestorableDiagnosticsProposal(proposal))
  const hasValidDraft = Boolean(proposal && !appliedMisPlanId && isRestorableDiagnosticsProposal(proposal))

  const compose = async () => {
    if (disabledReason || !misPatientId || pending) return
    setPending(true)
    setError(null)
    setAppliedMisPlanId(null)
    void persistCheckpoint({ step: 'compose_in_progress' })
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          action_key: ACTION_KEY,
          ui_surface: UI_SURFACE,
          source_kind: 'text',
          input_text: PLAN_DIAGNOSTICS_INITIAL_PROMPT,
          patient_id: misPatientId,
          target_entity_id: plan?.mis_plan_id ?? null,
          metadata: {
            consumer: 'staff_lk_ai',
            authoring_clinician_id: staffUserId,
            workspace_target: 'constructor',
            output_mode: 'staged',
            model_choice: PLAN_DIAGNOSTICS_MODEL_CHOICE,
          },
        }),
      })
      if (!res.ok) throw new Error(`compose HTTP ${res.status}`)
      const data = (await res.json()) as { proposal: AiProposal }
      setProposal(data.proposal)
      void persistCheckpoint({ step: 'proposal_ready', proposal: data.proposal as Record<string, unknown>, last_error: null })
    } catch (e) {
      const err = String(e)
      try {
        const recovered = await recoverLatestDiagnosticsProposal({ apiBase, authHeader, misPatientId })
        if (recovered) {
          setProposal(recovered as AiProposal)
          void persistCheckpoint({
            step: 'proposal_ready',
            proposal: recovered as Record<string, unknown>,
            last_error: null,
            recovered_after_timeout: true,
          })
          setError('Compose ответил ошибкой, но черновик уже готов в MIS — показан последний результат.')
          return
        }
      } catch {
        /* recovery optional */
      }
      setError(err)
      void persistCheckpoint({ step: 'compose_failed', last_error: err, proposal: proposal as Record<string, unknown> | null ?? savedCheckpoint?.proposal ?? null })
    } finally {
      setPending(false)
    }
  }

  const handleApplied = (payload: unknown) => {
    const planId = extractMisPlanId(payload)
    if (planId) {
      setAppliedMisPlanId(planId)
      void prefetchPresentationPhotoSources({
        bootstrap,
        session,
        patientId: misPatientId!,
        planId,
      })
    }
    void persistCheckpoint({
      step: 'applied',
      proposal: proposal as Record<string, unknown> | null,
      applied_mis_plan_id: planId,
      last_error: null,
    })
    onApplied?.()
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded-md bg-slate-800 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          disabled={Boolean(disabledReason) || pending || hasValidDraft}
          onClick={() => void compose()}
        >
          {pending ? 'Составляем…' : 'Составить план лечения'}
        </button>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">Бета</span>
        {(hasValidDraft || hasStaleDraft) ? (
          <button
            type="button"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700"
            disabled={pending}
            onClick={() => dismissDraft()}
          >
            Отклонить черновик
          </button>
        ) : null}
      </div>

      {disabledReason ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{disabledReason}</p>
      ) : null}
      {diagnocatHint ? (
        <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">{diagnocatHint}</p>
      ) : (
        <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          Пациент связан с диагностикой. Черновик можно уточнить в блоке ниже, затем применить.
        </p>
      )}

      {savedCheckpoint && isRestorableDiagnosticsProposal(savedCheckpoint.proposal) && (savedCheckpoint.step === 'proposal_ready' || savedCheckpoint.step === 'compose_failed') ? (
        <p className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900">
          Сохранён черновик от {formatCheckpointSavedAt(savedCheckpoint) ?? 'ранее'}. Можно продолжить без повторного compose.
        </p>
      ) : null}

      {hasStaleDraft ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Черновик пустой или устарел (не от Gemini). Нажмите «Отклонить черновик» и составьте план заново.
        </p>
      ) : null}

      {error ? (
        <div className="space-y-1">
          <p className="text-xs text-red-600">{error}</p>
          <p className="text-[11px] text-slate-500">Черновик сохранён в плане — обновите страницу, чтобы продолжить с последнего результата.</p>
        </div>
      ) : null}

      {hasValidDraft && misPatientId ? (
        <PlanDiagnosticsReviewPanel
          apiBase={apiBase}
          session={session}
          misPatientId={misPatientId}
          staffUserId={staffUserId}
          uiSurface={UI_SURFACE}
          proposal={proposal!}
          onProposalChange={setProposal}
          onApplied={handleApplied}
        />
      ) : null}

      {appliedMisPlanId && misPatientId ? (
        <>
          <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            План создан (MIS {appliedMisPlanId.slice(0, 8)}…).
          </p>
          <PlanGammaPresentationStep
            bootstrap={bootstrap}
            session={session}
            misPatientId={misPatientId}
            planId={appliedMisPlanId}
            planDetail={plan ?? null}
          />
        </>
      ) : null}
    </div>
  )
}
